import { computed, Injectable, signal, inject } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  AuthError,
  BrowserAuthError,
  InteractionRequiredAuthError,
  InteractionRequiredAuthErrorCodes,
  InteractionStatus,
} from '@azure/msal-browser';
import { catchError, concatMap, filter, finalize, map, Observable, of, switchMap, take } from 'rxjs';
import { IrisAuthService } from '@iris/modules/auth/utils/auth.service';
import { getAzureScopes } from './msal.config';
import { AuthFacade } from '@iris/modules/auth/utils/auth.facade';
import { toObservable } from '@angular/core/rxjs-interop';
import { IrisLocalStorageService } from '@iris/common/services/utils/iris-local-storage.service';
import { IrisUserService } from '@iris/common/services/user.service';
import { IrisConfirmationService } from '@iris/common/services/confirmation.service';

export interface IrisMsalAcquireTokenParamsI {
  initialLogin?: boolean;
  forceRefresh?: boolean;
}

@Injectable({ providedIn: 'root' })
export class IrisMsalService {
  private readonly msalService = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);
  private readonly authFacade = inject(AuthFacade);
  private readonly authService = inject(IrisAuthService);
  private readonly localStorage = inject(IrisLocalStorageService);
  private readonly userService = inject(IrisUserService);
  private readonly confirmationService = inject(IrisConfirmationService);

  private readonly activeRequest$ = signal<Observable<boolean>>(null);
  private readonly azureAuthResult = signal<AuthenticationResult>(null);
  readonly azureAccessToken = computed(() => this.azureAuthResult()?.accessToken);
  readonly azureTokenExpiresOn = computed(() => this.azureAuthResult()?.expiresOn);
  readonly azureAccessToken$ = toObservable(this.azureAccessToken).pipe(
    switchMap(accessToken => {
      // We need to check SSO active status here to prevent unnecessary token acquisition attempts
      if (!this.userService.me.ssoActive) {
        return of(null);
      }
      if (this.azureTokenExpired || !accessToken) {
        return this.acquireTokenSilent().pipe(
          map(() => this.azureAccessToken()),
        );
      }
      return of(accessToken);
    }),
  );
  readonly azureIdToken = computed(() => this.azureAuthResult()?.idToken);
  readonly azureIdToken$ = toObservable(this.azureIdToken).pipe(
    switchMap(idToken => {
      // We need to check SSO active status here to prevent unnecessary token acquisition attempts
      if (!this.userService.me.ssoActive) {
        return of(null);
      }
      if (this.azureTokenExpired || !idToken) {
        return this.acquireTokenSilent().pipe(
          map(() => this.azureIdToken()),
        );
      }
      return of(idToken);
    }),
  );

  private get azureTokenExpired(): boolean {
    return this.azureTokenExpiresOn() <= new Date();
  }

  handleAzureAccount(): Observable<boolean> {
    return this.msalService.initialize().pipe(
      concatMap(() => this.msalService.handleRedirectObservable()),
      take(1),
      switchMap(() => {
        const account = this.checkAndSetActiveAccount();
        if (!account) {
          this.msalService.loginRedirect();
        }
        return of(!!account);
      }),
      catchError((err) => {
        console.error('msalService.handleRedirectObservable():', err);
        return of(false);
      }),
    );
  }

  checkAndSetActiveAccount(): AccountInfo {
    if (!this.msalService.instance.getActiveAccount() && this.msalService.instance.getAllAccounts()?.length > 0) {
      this.msalService.instance.setActiveAccount(this.msalService.instance.getAllAccounts()[0]);
    }

    return this.msalService.instance.getActiveAccount();
  }

  acquireTokenRedirect(): Observable<boolean> {
    return this.msalService.handleRedirectObservable().pipe(
      switchMap(() => this.msalBroadcastService.inProgress$.pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
      )),
      switchMap(() => this.msalService.acquireTokenRedirect({
        scopes: getAzureScopes(this.authService),
        redirectUri: this.authService.getMsalRedirectUri(),
      })),
      map(() => true),
      catchError((err: AuthError) => {
        console.error('msalService.acquireTokenRedirect():', err);
        return this.handleInteractionError(err);
      }),
    );
  }

  acquireTokenPopup(): Observable<boolean> {
    return this.msalService.acquireTokenPopup({
      scopes: getAzureScopes(this.authService),
    }).pipe(
      map((res: AuthenticationResult) => {
        this.azureAuthResult.set(res);
        return true;
      }),
      catchError((err: AuthError) => {
        console.error('msalService.acquireTokenPopup():', err);
        return this.handleInteractionError(err);
      }),
    );
  }

  acquireTokenSilent(params?: IrisMsalAcquireTokenParamsI): Observable<boolean> {
    const initialLogin = params?.initialLogin ?? false;
    const forceRefresh = params?.forceRefresh ?? false;
    const activeAccount = this.checkAndSetActiveAccount();
    if (!activeAccount) {
      this.localStorage.setItem('lastHref', location.href);
      if (initialLogin) {
        return this.acquireTokenRedirect();
      }
      return this.acquireTokenPopup();
    }
    if (this.activeRequest$()) {
      return this.activeRequest$();
    }

    const request = this.msalService.initialize().pipe(
      concatMap(() => this.msalService.acquireTokenSilent({
        scopes: getAzureScopes(this.authService, initialLogin),
        redirectUri: this.authService.getMsalRedirectUri(),
        account: activeAccount,
        forceRefresh,
      })),
      switchMap((res: AuthenticationResult) => {
        this.azureAuthResult.set(res);
        if (!initialLogin) {
          return of(true);
        }
        return this.authFacade.loginAzure(res.accessToken).pipe(
          map(() => true),
        );
      }),
      catchError((err: AuthError) => {
        console.error('msalService.acquireTokenSilent():', err);
        return this.handleInteractionError(err, !initialLogin);
      }),
      finalize(() => this.activeRequest$.set(null)),
    );
    this.activeRequest$.set(request);
    return request;
  }

  removeActiveAccount(): void {
    void this.msalService.instance.clearCache();
    this.msalService.instance.setActiveAccount(null);
  }

  private handleInteractionError(err: AuthError, usePopup = false): Observable<boolean> {
    const isInteractionRequired = err instanceof InteractionRequiredAuthError && err.errorCode === InteractionRequiredAuthErrorCodes.interactionRequired;
    if (isInteractionRequired || err instanceof BrowserAuthError) {
      this.removeActiveAccount();
      this.clearCache();
      return usePopup ? this.acquireTokenPopup() : this.acquireTokenRedirect();
    }
    return of(false);
  }

  clearCache(): void {
    const msalKeys = [
      ...Object.keys(localStorage),
      ...Object.keys(sessionStorage),
    ].filter(key => key.startsWith('msal.') || key.startsWith('iris_access') || key.startsWith('iris_refresh'));

    msalKeys.forEach(key => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });

    document.cookie.split(';').forEach((c) => {
      if (c.trim().startsWith('msal.')) {
        document.cookie = c
          .replace(/^ +/, '')
          .replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
      }
    });
  }
}
